import type { ComponentDefinition, ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';

import { shouldEnableBuyAgainDefaultForNewSites } from '../../../../utils/experiments';
import { getSiteLocale, isRtlLocale } from '../../../../utils/locale';
import { APP_TOKEN, MENU_IDS } from '../../../constants';
import {
  LOGIN_BAR_CONTROLLER_TYPE,
  LOGIN_COMP_APP_WIDGET_DEF_ECOM_V2,
  LOGIN_COMP_APP_WIDGET_DEF_V2,
} from '../../../constants/layouts/login-bar/classic-login-bar-layouts';
import { COMMON_LOGIN_BAR_COMP_DATA } from '../../../constants/layouts/login-bar/common-login-bar-layouts';
import { getLoginBarBuilderEditorCompDef } from '../../../constants/layouts/login-bar/editor3-login-bar-layouts';
import { LOGIN_COMP_RESPONSIVE_APP_WIDGET_DEF_V2 } from '../../../constants/layouts/login-bar/responsive-login-bar-layouts';
import { getIsResponsiveEditor } from '../../../services/applicationState';

const getLoginBarWidgetCompDef = (isResponsiveEditor: boolean, isStoresInstallation: boolean) => {
  if (isResponsiveEditor) {
    return LOGIN_COMP_RESPONSIVE_APP_WIDGET_DEF_V2;
  }

  if (isStoresInstallation) {
    return LOGIN_COMP_APP_WIDGET_DEF_ECOM_V2;
  }

  return LOGIN_COMP_APP_WIDGET_DEF_V2;
};

const getLoginBarCompDef = async (
  isResponsiveEditor: boolean,
  isStoresInstallation: boolean,
  isBuilderEditor: boolean,
  isFirstInstall: boolean,
) => {
  if (isBuilderEditor) {
    return getLoginBarBuilderEditorCompDef();
  }

  const widgetCompDef = getLoginBarWidgetCompDef(isResponsiveEditor, isStoresInstallation);
  const component = widgetCompDef.components?.[0];

  const enableBuyAgainByDefault = isFirstInstall && (await shouldEnableBuyAgainDefaultForNewSites());
  const data = enableBuyAgainByDefault
    ? { ...COMMON_LOGIN_BAR_COMP_DATA, showBuyAgain: true }
    : COMMON_LOGIN_BAR_COMP_DATA;

  return {
    ...widgetCompDef,
    components: [{ ...component, data }],
  } as ComponentDefinition;
};

export async function addLoginButton(
  editorSDK: EditorSDK,
  headerRef: ComponentRef,
  isStoresInstallation: boolean = true,
  isFirstInstall: boolean = false,
) {
  const isResponsiveEditor = getIsResponsiveEditor();
  const isBuilderEditor = await editorSDK.info.isBuilderComponentModelSupported('');
  const componentDefinition = await getLoginBarCompDef(
    isResponsiveEditor,
    isStoresInstallation,
    isBuilderEditor,
    isFirstInstall,
  );

  const compRef = await editorSDK.components.add(APP_TOKEN, {
    pageRef: headerRef,
    componentDefinition,
    // @ts-expect-error
    showAddToHeaderPanel: false,
  });

  if (!compRef) {
    throw new Error('Failed to add the login bar component');
  }

  return compRef;
}

const updateLoginBarItemsAlignment = async (editorSDK: EditorSDK, loginBarRef: ComponentRef) => {
  const locale = await getSiteLocale(editorSDK);

  return editorSDK.components.properties.update(APP_TOKEN, {
    componentRef: loginBarRef,
    props: { itemsAlignment: isRtlLocale(locale) ? 'right' : 'left' },
  });
};

const connectLoginButton = async (editorSDK: EditorSDK, componentRef: ComponentRef) => {
  const compData = (await editorSDK.components.data.get('', { componentRef })) as { iconItemsRef; menuItemsRef };

  if (compData.iconItemsRef && compData.menuItemsRef) {
    return;
  }

  // To do: It should be possible to have this data in the definiton itself, so less handling would happen here
  const dataToUpdate = {
    menuItemsRef: {
      menuRef: '#' + MENU_IDS.LOGIN_MENU_ID,
      type: 'CustomMenuDataRef',
    },
    iconItemsRef: {
      menuRef: '#' + MENU_IDS.LOGIN_ICONS_MENU_ID,
      type: 'CustomMenuDataRef',
    },
  };

  await editorSDK.components.data.update(APP_TOKEN, { componentRef, data: dataToUpdate });
};

export const handleLoginBarAdded = async (editorSDK: EditorSDK, eventPayload?: any) => {
  if (!eventPayload?.componentRef) {
    return;
  }

  const [controllerConnection] = await editorSDK.controllers.getControllerConnections('', {
    controllerRef: eventPayload.componentRef,
  });

  if (controllerConnection?.connection.role !== LOGIN_BAR_CONTROLLER_TYPE) {
    return;
  }

  const loginBarRef = controllerConnection.componentRef;

  return Promise.all([
    updateLoginBarItemsAlignment(editorSDK, loginBarRef),
    connectLoginButton(editorSDK, loginBarRef),
  ]);
};
